import { Briefcase, Plus, X } from 'lucide-react';
import { useState, type ReactElement } from 'react';

import { cn } from '@/components/ui/cn';
import { ConfirmModal } from '@/components/ui/ConfirmModal';
import { useToast } from '@/components/ui/toast';
import {
  RESPONSIBILITY_ROLE_LABELS,
  type DossierResponsibility,
} from '@/features/document-dossier/types';
import { personColorIndex } from '@/features/master-list/types';
import { usePermission } from '@/hooks/usePermission';
import type { DocumentStatus } from '@/lib/document-status';

import { ROLE_STAGE, responsibilityError } from '../presentation';
import { RESPONSIBILITY_ROLES, type ResponsibilityRole } from '../types';
import { useRemoveResponsibility } from '../use-responsibilities';
import { AssignResponsibilityModal } from './AssignResponsibilityModal';

// Responsables de la ficha (spec 2026-10-14 §1): los cuatro roles siempre visibles,
// cada uno con sus personas o puestos. De ellos salen los avisos por etapa, por eso un
// rol vacío dice la consecuencia. Calidad asigna por rol y quita con confirmación.

const LOCKED: Partial<Record<DocumentStatus, string>> = {
  obsoleto: 'El documento está obsoleto: sus responsables ya no se cambian.',
  anulado: 'El documento está anulado: sus responsables ya no se cambian.',
};

function nameOf(r: DossierResponsibility): string {
  return r.displayName ?? 'Persona dada de baja';
}

function Assignee({ r }: { r: DossierResponsibility }): ReactElement {
  if (r.kind === 'position') {
    return (
      <div className="who">
        <span className="av sm p0" aria-hidden="true">
          <Briefcase size={12} />
        </span>
        <span className="nm" title={nameOf(r)}>
          {nameOf(r)}
        </span>
        <span className="dim-mono">Puesto</span>
      </div>
    );
  }
  return (
    <div className="who">
      <span className={cn('av sm', `p${personColorIndex(r.userId ?? '')}`)} aria-hidden="true">
        {r.initials ?? '?'}
      </span>
      <span className="nm" title={nameOf(r)}>
        {nameOf(r)}
      </span>
      {!r.typeAccess && (
        <span
          className="badge b-warn"
          title="Una regla de este tipo de documento ya no le permite consultarlo: no recibe sus avisos. Asigna a otra persona."
        >
          Sin acceso a este tipo
        </span>
      )}
    </div>
  );
}

export function ResponsibilitiesCard({
  entryId,
  code,
  status,
  responsibilities,
}: {
  entryId: string;
  code: string;
  status: DocumentStatus;
  responsibilities: DossierResponsibility[];
}): ReactElement {
  const canManage = usePermission('quality:document:metadata');
  const remove = useRemoveResponsibility(entryId);
  const { showToast } = useToast();
  const [assigning, setAssigning] = useState<ResponsibilityRole | null>(null);
  const [removing, setRemoving] = useState<DossierResponsibility | null>(null);
  const locked = LOCKED[status];
  const canEdit = canManage && locked === undefined;

  async function confirmRemove(): Promise<void> {
    if (removing === null) {
      return;
    }
    try {
      await remove.mutateAsync(removing.id);
      showToast(
        `${nameOf(removing)} ya no es responsable de la ${ROLE_STAGE[removing.role]} de ${code}`,
      );
      setRemoving(null);
    } catch (err) {
      showToast(responsibilityError(err), 'error');
    }
  }

  return (
    <div className="card rise d2">
      <div className="ch">
        <h3>Responsables</h3>
        <span className="count">avisos por etapa</span>
      </div>
      <div className="cb resp-card">
        {canManage && locked !== undefined && <p className="soft resp-note">{locked}</p>}
        {RESPONSIBILITY_ROLES.map((role) => {
          const label = RESPONSIBILITY_ROLE_LABELS[role];
          const rows = responsibilities.filter((r) => r.role === role);
          return (
            <div key={role} role="group" aria-label={label} className="resp-row">
              <span className="resp-label">{label}</span>
              <div className="resp-people">
                {rows.length === 0 ? (
                  <div className="resp-empty">
                    <span className="dim-mono">Sin responsable</span>
                    <span className="soft">
                      Los avisos de la {ROLE_STAGE[role]} los atiende Calidad mientras no se asigne.
                    </span>
                  </div>
                ) : (
                  <ul className="resp-list">
                    {rows.map((r) => (
                      <li key={r.id} className="resp-item">
                        <Assignee r={r} />
                        {canEdit && (
                          <button
                            type="button"
                            className="icon-btn"
                            aria-label={`Quitar a ${nameOf(r)} como ${label.toLowerCase()}`}
                            onClick={() => setRemoving(r)}
                          >
                            <X size={14} aria-hidden="true" />
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              {canEdit && (
                <button
                  type="button"
                  className="icon-btn resp-add"
                  aria-label={`Asignar ${label.toLowerCase()}`}
                  title={`Asignar ${label.toLowerCase()}`}
                  onClick={() => setAssigning(role)}
                >
                  <Plus size={15} aria-hidden="true" />
                </button>
              )}
            </div>
          );
        })}
      </div>
      {assigning !== null && (
        <AssignResponsibilityModal
          entryId={entryId}
          code={code}
          role={assigning}
          onClose={() => setAssigning(null)}
        />
      )}
      <ConfirmModal
        open={removing !== null}
        title="Quitar responsable"
        message={
          removing === null
            ? ''
            : `${nameOf(removing)} dejará de recibir los avisos de la ${ROLE_STAGE[removing.role]} de ${code}. Se puede volver a asignar.`
        }
        confirmLabel="Quitar"
        destructive
        busy={remove.isPending}
        onConfirm={() => void confirmRemove()}
        onCancel={() => setRemoving(null)}
      />
    </div>
  );
}
